import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Users, Send, DollarSign, Mail, Loader2, Plus, Trash2, Image, Ban } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AdminAffiliates() {
  const { t } = useI18n();
  const [affiliates, setAffiliates] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMail, setShowMail] = useState(false);
  const [showAddBanner, setShowAddBanner] = useState(false);
  const [mailForm, setMailForm] = useState({ subject: "", message: "" });
  const [bannerForm, setBannerForm] = useState({ name: "", image_url: "", width: 728, height: 90, landing_url: "/register" });
  const [sending, setSending] = useState(false);

  const fetchData = () => {
    Promise.all([
      api.get("/affiliate/admin/overview"),
      api.get("/affiliate/admin/banners"),
    ]).then(([a, b]) => { setAffiliates(a.data); setBanners(b.data); })
      .catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const handleMassMail = async () => {
    setSending(true);
    try {
      const res = await api.post("/affiliate/admin/mass-mail", mailForm);
      toast.success(`${res.data.sent} e-mails verzonden`);
      setShowMail(false);
      setMailForm({ subject: "", message: "" });
    } catch (err) { toast.error(err.response?.data?.detail || t("common.error")); }
    finally { setSending(false); }
  };

  const handlePayout = async () => {
    try {
      const res = await api.post("/affiliate/admin/process-payouts");
      toast.success(res.data.message);
    } catch { toast.error(t("common.error")); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;

  const totalCommission = affiliates.reduce((sum, a) => sum + (a.total_commission || 0), 0);
  const totalPaid = affiliates.reduce((sum, a) => sum + (a.total_paid || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-affiliates-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-emerald-500" />
          <div>
            <h1 className="text-2xl font-bold">{t("admin.affiliates_title")}</h1>
            <p className="text-sm text-muted-foreground">{affiliates.length} {t("admin.affiliates_subtitle")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={showMail} onOpenChange={setShowMail}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="mass-mail-btn"><Mail className="w-4 h-4 mr-2" />{t("admin.mass_mailing")}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>{t("admin.mass_mail_title")}</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground">{t("admin.mass_mail_desc").replace("{{name}}", "{{name}}").replace("{{code}}", "{{code}}")}</p>
              <div className="space-y-4 py-2">
                <div><Label>{t("admin.subject")}</Label><Input value={mailForm.subject} onChange={(e) => setMailForm({...mailForm, subject: e.target.value})} className="mt-1" data-testid="mass-mail-subject" /></div>
                <div><Label>{t("admin.message")}</Label><Textarea rows={8} value={mailForm.message} onChange={(e) => setMailForm({...mailForm, message: e.target.value})} className="mt-1" data-testid="mass-mail-body" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowMail(false)}>{t("admin.cancel")}</Button>
                <Button onClick={handleMassMail} disabled={sending || !mailForm.subject || !mailForm.message} className="bg-blue-600" data-testid="send-mass-mail-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" />{t("admin.send_to")} {affiliates.length} {t("admin.affiliates_subtitle")}</>}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button onClick={handlePayout} className="bg-emerald-600 hover:bg-emerald-700" data-testid="process-payouts-btn">
            <DollarSign className="w-4 h-4 mr-2" />{t("admin.process_payouts")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: t("admin.total_affiliates"), val: affiliates.length, color: "text-blue-500" },
          { label: t("admin.total_referrals"), val: affiliates.reduce((s, a) => s + (a.referral_count || 0), 0), color: "text-emerald-500" },
          { label: t("admin.total_commission"), val: `${totalCommission.toFixed(2)} EUR`, color: "text-amber-500" },
          { label: t("admin.paid_out"), val: `${totalPaid.toFixed(2)} EUR`, color: "text-purple-500" },
        ].map((s, i) => (
          <Card key={i} className="bg-card border-border"><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{s.label}</p>
            <p className={`text-2xl font-bold ${s.color}`}>{s.val}</p>
          </CardContent></Card>
        ))}
      </div>

      <Card className="bg-card border-border overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("admin.affiliate_name")}</TableHead>
                <TableHead>{t("admin.email")}</TableHead>
                <TableHead>Code</TableHead>
                <TableHead className="text-right">{t("admin.commission_pct")}</TableHead>
                <TableHead className="text-right">{t("admin.referrals")}</TableHead>
                <TableHead className="text-right">{t("admin.commission_eur")}</TableHead>
                <TableHead>{t("admin.status")}</TableHead>
                <TableHead className="text-right">{t("admin.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {affiliates.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">{t("admin.no_affiliates")}</TableCell></TableRow>
              ) : affiliates.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-medium">{a.user?.name || "-"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{a.user?.email || "-"}</TableCell>
                  <TableCell className="font-mono-data text-sm">{a.code}</TableCell>
                  <TableCell className="text-right">
                    <Select defaultValue={String(a.commission_pct || 15)} onValueChange={async (v) => {
                      await api.put(`/affiliate/admin/${a.id}`, { commission_pct: parseFloat(v) });
                      toast.success(`${t("admin.commission_changed")} ${v}%`);
                      fetchData();
                    }}>
                      <SelectTrigger className="w-20 h-7 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[10, 15, 20, 25, 30].map(v => <SelectItem key={v} value={String(v)}>{v}%</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right font-mono-data">{a.referral_count || 0}</TableCell>
                  <TableCell className="text-right font-mono-data">{(a.total_commission || 0).toFixed(2)} EUR</TableCell>
                  <TableCell><Badge className={a.status === "active" ? "bg-emerald-500/20 text-emerald-400 border-0" : a.status === "blocked" ? "bg-red-500/20 text-red-400 border-0" : ""}>{a.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {a.status !== "blocked" ? (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-500" title={t("admin.block")} onClick={async () => {
                          await api.post(`/affiliate/admin/${a.id}/block`);
                          toast.success(t("admin.blocked"));
                          fetchData();
                        }}><Ban className="w-3.5 h-3.5" /></Button>
                      ) : (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-500" title={t("admin.unblock")} onClick={async () => {
                          await api.put(`/affiliate/admin/${a.id}`, { status: "active" });
                          toast.success(t("admin.unblocked"));
                          fetchData();
                        }}><Users className="w-3.5 h-3.5" /></Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" title={t("admin.delete")} onClick={async () => {
                        if (!window.confirm(t("admin.delete_affiliate"))) return;
                        await api.delete(`/affiliate/admin/${a.id}`);
                        toast.success(t("admin.deleted"));
                        fetchData();
                      }}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2"><Image className="w-4 h-4" />{t("admin.affiliate_banners")}</CardTitle>
          <Dialog open={showAddBanner} onOpenChange={setShowAddBanner}>
            <DialogTrigger asChild><Button size="sm" className="bg-blue-600"><Plus className="w-3.5 h-3.5 mr-1.5" />{t("admin.add_banner")}</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("admin.new_banner")}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div><Label>{t("admin.banner_name")}</Label><Input value={bannerForm.name} onChange={(e) => setBannerForm({...bannerForm, name: e.target.value})} className="mt-1" /></div>
                <div><Label>{t("admin.banner_url")}</Label><Input value={bannerForm.image_url} onChange={(e) => setBannerForm({...bannerForm, image_url: e.target.value})} placeholder="https://..." className="mt-1 font-mono-data text-sm" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>{t("admin.banner_width")}</Label><Input type="number" value={bannerForm.width} onChange={(e) => setBannerForm({...bannerForm, width: parseInt(e.target.value)})} className="mt-1" /></div>
                  <div><Label>{t("admin.banner_height")}</Label><Input type="number" value={bannerForm.height} onChange={(e) => setBannerForm({...bannerForm, height: parseInt(e.target.value)})} className="mt-1" /></div>
                </div>
                <div><Label>{t("admin.landing_url")}</Label><Input value={bannerForm.landing_url} onChange={(e) => setBannerForm({...bannerForm, landing_url: e.target.value})} placeholder="/register" className="mt-1" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddBanner(false)}>{t("admin.cancel")}</Button>
                <Button className="bg-blue-600" onClick={async () => {
                  try {
                    await api.post("/affiliate/admin/banners", bannerForm);
                    toast.success(t("admin.add") + " - OK");
                    setShowAddBanner(false);
                    setBannerForm({ name: "", image_url: "", width: 728, height: 90, landing_url: "/register" });
                    fetchData();
                  } catch { toast.error(t("common.error")); }
                }}>{t("admin.add")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {banners.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">{t("admin.no_banners")}</p>
          ) : (
            <div className="space-y-4">
              {banners.map((b) => (
                <div key={b.id} className="flex items-center gap-4 p-3 border border-border rounded-lg">
                  <div className="w-24 h-16 bg-accent/30 rounded flex items-center justify-center overflow-hidden shrink-0">
                    {b.image_url ? <img src={b.image_url} alt={b.name} className="max-w-full max-h-full" /> : <Image className="w-6 h-6 text-muted-foreground" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{b.name}</p>
                    <p className="text-xs text-muted-foreground">{b.width}x{b.height}px</p>
                  </div>
                  <Badge variant={b.active ? "default" : "secondary"} className={b.active ? "bg-emerald-500/20 text-emerald-400 border-0" : ""}>{b.active ? t("admin.active") : t("admin.inactive")}</Badge>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={async () => {
                    await api.delete(`/affiliate/admin/banners/${b.id}`);
                    toast.success(t("admin.deleted"));
                    fetchData();
                  }}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
